import { ReactComponent as ActionItemsIcon } from 'assets/menu/ActionItems.svg';
import { ReactComponent as ChartIcon } from 'assets/menu/Chart.svg';
import { ReactComponent as ContactsIcon } from 'assets/menu/Contacts.svg';
import { ReactComponent as DeliveryIcon } from 'assets/menu/DeliveryTruck.svg';
import { ReactComponent as DesktopIcon } from 'assets/menu/Desktop.svg';
import { ReactComponent as HistoryIcon } from 'assets/menu/History.svg';
import { ReactComponent as OrdersIcon } from 'assets/menu/SurgeryList.svg';
import { ReactComponent as TurnaroundIcon } from 'assets/menu/Turnaround.svg';
import { ReactComponent as UserIcon } from 'assets/menu/User.svg';
import { ACTION_ITEMS_V2_ON, NEW_ORDER_PAGE_ON } from 'featureFlags';
import { lazy as asyncLoad } from 'react';
import ActionItemsV1 from 'components/ActionItemsPage';
import ActionItems from 'pages/ActionItems';
import { LoggedOutReason } from 'hooks/useAuth';
import { LocaleRoutes } from 'locale/locale';
import { CheckInTrayDecantDetails } from 'pages/CheckInTrayDecantDetails/CheckInTrayDecantDetails';
import { CheckInTrayDecantImageCapture } from 'pages/CheckInTrayDecantImageCapture/CheckInTrayDecantImageCapture';
import { CheckInTrayDetailsPage } from 'pages/CheckInTrayDetails/CheckInTrayDetailsPage';
import { CheckInTrayImageCapture } from 'pages/CheckInTrayImageCapture/CheckInTrayImageCapture';
import { CheckOutTrayImageCapture } from 'pages/CheckOutTrayImageCapture/CheckOutTrayImageCapture';
import LoggedOutPage from 'pages/LoggedOut/LoggedOutPage';
import { TurnaroundPage } from 'pages/Turnaround/Turnaround';
import { TurnaroundListPage } from 'pages/TurnaroundList/TurnaroundList';
import { AppRoute } from 'typings/AppRoute';
import { Role } from 'utils/helpers/auth0.helpers';
import { hospitalRoles, supplierRoles, systemAdminRoles } from 'utils/helpers/permissions.helpers';

export type RouteName = keyof LocaleRoutes | 'NotFound' | 'ColorPalette';

type TRoutes = Record<RouteName, AppRoute>;

// lazy/background load components, for startup speed
const CaptureTrayImagePage = asyncLoad(() => import('pages/CaptureTrayImage/CaptureTrayImage'));
const TrayPictureListPage = asyncLoad(() => import('pages/TrayPictureList/TrayPictureList'));
const LastTrayInspectionPage = asyncLoad(() => import('pages/LastTrayInspection/LastTrayInspection'));
const CheckInPage = asyncLoad(() => import('pages/CheckIn'));
const ImplantsPage = asyncLoad(() => import('pages/Implants/ImplantsPage'));
const ImplantList = asyncLoad(() => import('pages/Implants/ImplantList'));
const CaptureNonScannableImplants = asyncLoad(() => import('pages/Implants/CaptureNonScannableImplants'));
const CheckInTrayPage = asyncLoad(() => import('pages/CheckIn/CheckInTray/CheckInTrayPage'));
const CheckOutPage = asyncLoad(() => import('pages/CheckOut'));
const SetDetailsPage = asyncLoad(() => import('pages/SetDetails'));
const CheckOutTrayPage = asyncLoad(() => import('pages/CheckOut/CheckOutTray'));
const CheckOutTraySubmitPage = asyncLoad(() => import('pages/CheckOut/CheckOutTraySubmit'));
const ColorPalettePage = asyncLoad(() => import('pages/ColorPalette'));
const DeliveryListPage = asyncLoad(() => import('pages/Delivieries/DeliveryList'));
const SurgeryListPage = asyncLoad(() => import('pages/SurgeryList/SurgeryListPage'));
const SurgeryPage = asyncLoad(() => import('pages/Surgery/SurgeryPage'));
const NotFoundPage = asyncLoad(() => import('pages/NotFound'));
const PastDeliveriesPage = asyncLoad(() => import('pages/PastDeliveries'));
const ReportsPage = asyncLoad(() => import('pages/Reports/ReportsPage'));
const ReportPage = asyncLoad(() => import('pages/Reports/ReportPage'));
const ViewDispatchPage = asyncLoad(() => import('pages/ViewDispatch'));
const UserManagementPage = asyncLoad(() => import('pages/UserManagement'));
const SettingsPage = asyncLoad(() => import('pages/Settings'));
const AdvancedSettingsPage = asyncLoad(() => import('pages/Settings/AdvancedSettings'));
const WorkflowSettingsPage = asyncLoad(() => import('pages/Settings/WorkflowSettings'));
const MergeSurgeonsPage = asyncLoad(() => import('pages/Settings/MergeSurgeons'));
const AllOrdersPage = asyncLoad(() => import('pages/AllOrders/AllOrdersPage'));
const OrdersPage = asyncLoad(() => import('pages/Orders/OrdersPage'));
const UploadOrdersPage = asyncLoad(() => import('pages/UploadOrders'));
const ReviewOrdersPage = asyncLoad(() => import('pages/ReviewOrders/ReviewOrders'));
const ConfirmInDockPage = asyncLoad(() => import('pages/ConfirmInDock/ConfirmInDockPage'));
const OrderPageV1 = asyncLoad(() => import('pages/OrderV1/Order'));
const OrderPage = asyncLoad(() => import('pages/Order/OrderPage'));
const OrderSetPage = asyncLoad(() => import('pages/OrderSet/OrderSet'));
const SurgeonsPage = asyncLoad(() => import('pages/Surgeons/Surgeons'));
const DeviceRepsPage = asyncLoad(() => import('pages/DeviceReps/DeviceReps'));
const LocationsPage = asyncLoad(() => import('pages/Settings/Locations'));
const UnrecognisedSurgeonsPage = asyncLoad(() => import('pages/Settings/UnrecognisedSurgeons'));
const ContactsPage = asyncLoad(() => import('pages/Contacts/Contacts'));

export const DELIVERY_ID_PARAM = ':deliveryId';
export const ORDER_RECORD_ID_PARAM = ':orderRecordId';
export const SET_ID_PARAM = ':setId';
export const TRAY_ID_PARAM = ':trayId';
export const CHECK_IN_TRAY_ID_PARAM = ':checkInTrayId';
const CHECK_IN_WORKFLOW_PATH = 'check-in-workflow';

export const isCheckInWorkflowPage = (path: string) => path.includes(CHECK_IN_WORKFLOW_PATH);

const DELIVERIES_ROLES: Role[] = [...hospitalRoles, ...systemAdminRoles];
const CUSTOMER_OPS_ROLES: Role[] = [Role.CustomerOps];
export const ORDERS_ROLES: Role[] = [...supplierRoles, ...systemAdminRoles];
export const HOSPITAL_MANAGER_ROLES: Role[] = [Role.SaviConnectManager, ...systemAdminRoles];
const SETTINGS_ROLES: Role[] = [...HOSPITAL_MANAGER_ROLES, ...supplierRoles];

const makePath = {
  noArgs: (routeName: RouteName) => () => routes[routeName].path,
  deliveryId: (routeName: RouteName) => (deliveryId: string) =>
    routes[routeName].path.replace(DELIVERY_ID_PARAM, deliveryId),
  deliveryIdSetId: (routeName: RouteName) => (deliveryId: string, setId: string) =>
    routes[routeName].path.replace(DELIVERY_ID_PARAM, deliveryId).replace(SET_ID_PARAM, setId),
  deliveryIdSetIdTrayId: (routeName: RouteName) => (deliveryId: string, setId: string, trayId: string) =>
    routes[routeName].path
      .replace(DELIVERY_ID_PARAM, deliveryId)
      .replace(SET_ID_PARAM, setId)
      .replace(TRAY_ID_PARAM, trayId)
      .replace(CHECK_IN_TRAY_ID_PARAM, trayId),
  orderId: (routeName: RouteName) => (orderId: string) =>
    routes[routeName].path.replace(ORDER_RECORD_ID_PARAM, orderId),
  orderIdSetId: (routeName: RouteName) => (orderId: string, setId: string) =>
    routes[routeName].path.replace(ORDER_RECORD_ID_PARAM, orderId).replace(SET_ID_PARAM, setId),
};

const routes: TRoutes = {
  ActionItemsV1: {
    title: 'Action Items',
    path: ACTION_ITEMS_V2_ON ? '/action-items' : '/',
    makePath: makePath.noArgs('ActionItemsV1'),
    component: ActionItemsV1,
    roles: DELIVERIES_ROLES,
    Icon: ActionItemsIcon,
    fill: 'path',
    userAuthState: 'Authenticated',
  },
  ActionItems: {
    title: 'Action Items',
    path: ACTION_ITEMS_V2_ON ? '/' : '/action-items',
    makePath: makePath.noArgs('ActionItems'),
    component: ActionItems,
    roles: [...DELIVERIES_ROLES, ...CUSTOMER_OPS_ROLES],
    Icon: ActionItemsIcon,
    fill: 'path',
    userAuthState: 'Authenticated',
  },
  Home: {
    title: 'Delivery List',
    path: '/delivery-list',
    makePath: makePath.noArgs('Home'),
    component: DeliveryListPage,
    roles: DELIVERIES_ROLES,
    Icon: DeliveryIcon,
    fill: 'svg',
    userAuthState: 'Authenticated',
  },
  SurgeryList: {
    title: 'Surgery List',
    path: '/surgery-list',
    makePath: makePath.noArgs('SurgeryList'),
    component: SurgeryListPage,
    roles: DELIVERIES_ROLES,
    Icon: OrdersIcon,
    fill: 'path',
    userAuthState: 'Authenticated',
  },
  AllOrders: {
    title: 'All Orders',
    path: '/all-orders',
    makePath: makePath.noArgs('AllOrders'),
    component: AllOrdersPage,
    // the page itself only admits tenant type 3 (Smartways) users
    roles: [...DELIVERIES_ROLES, ...CUSTOMER_OPS_ROLES],
    Icon: OrdersIcon,
    fill: 'path',
    userAuthState: 'Authenticated',
  },
  Surgery: {
    title: 'Surgery',
    path: `/surgery/${ORDER_RECORD_ID_PARAM}`,
    makePath: makePath.orderId('Surgery'),
    component: SurgeryPage,
    roles: [...ORDERS_ROLES, ...DELIVERIES_ROLES, ...CUSTOMER_OPS_ROLES],
  },
  PastDeliveries: {
    title: 'Past Deliveries',
    path: '/past-deliveries',
    makePath: makePath.noArgs('PastDeliveries'),
    component: PastDeliveriesPage,
    roles: DELIVERIES_ROLES,
    Icon: HistoryIcon,
    fill: 'path',
    userAuthState: 'Authenticated',
  },
  PastSurgeries: {
    title: 'Past Surgeries',
    path: '/past-surgeries',
    makePath: makePath.noArgs('PastSurgeries'),
    component: SurgeryListPage,
    roles: DELIVERIES_ROLES,
    Icon: HistoryIcon,
    fill: 'path',
    userAuthState: 'Authenticated',
  },
  Reports: {
    title: 'Reports',
    path: '/reports',
    makePath: makePath.noArgs('Reports'),
    component: ReportsPage,
    roles: HOSPITAL_MANAGER_ROLES,
    Icon: ChartIcon,
    fill: 'svg',
    userAuthState: 'Authenticated',
  },
  Report: {
    title: 'Report',
    path: '/reports/:reportId/:pageId',
    makePath: (reportId: string, pageId: string) =>
      routes.Report.path.replace(':reportId', reportId).replace(':pageId', pageId),
    component: ReportPage,
    roles: HOSPITAL_MANAGER_ROLES,
    Icon: ChartIcon,
    fill: 'svg',
    userAuthState: 'Authenticated',
  },
  UserPortal: {
    title: 'User Portal',
    path: '/user-portal',
    makePath: makePath.noArgs('UserPortal'),
    Icon: UserIcon,
    component: UserManagementPage,
    roles: SETTINGS_ROLES,
    fill: 'path',
    userAuthState: 'Logged',
  },
  Settings: {
    title: 'Settings',
    path: '/settings',
    makePath: makePath.noArgs('Settings'),
    component: SettingsPage,
    roles: SETTINGS_ROLES,
    fill: 'path',
    userAuthState: 'Authenticated',
  },
  MergeSurgeons: {
    title: 'Settings',
    path: '/settings/merge-surgeons',
    makePath: makePath.noArgs('MergeSurgeons'),
    component: MergeSurgeonsPage,
    roles: SETTINGS_ROLES,
    fill: 'path',
    userAuthState: 'Authenticated',
  },
  AdvancedSettings: {
    title: 'Settings',
    path: '/settings/advanced',
    makePath: makePath.noArgs('AdvancedSettings'),
    component: AdvancedSettingsPage,
    roles: SETTINGS_ROLES,
    fill: 'path',
    userAuthState: 'Authenticated',
  },
  WorkflowSettings: {
    title: 'Settings',
    path: '/settings/workflow',
    makePath: makePath.noArgs('WorkflowSettings'),
    component: WorkflowSettingsPage,
    roles: SETTINGS_ROLES,
    fill: 'path',
    userAuthState: 'Authenticated',
  },
  Locations: {
    title: 'Settings',
    path: '/settings/locations',
    makePath: makePath.noArgs('Locations'),
    component: LocationsPage,
    roles: [Role.MkSystemAdmin],
    fill: 'path',
    userAuthState: 'Authenticated',
  },
  UnrecognisedSurgeons: {
    title: 'Settings',
    path: '/settings/unrecognised-surgeons',
    makePath: makePath.noArgs('UnrecognisedSurgeons'),
    component: UnrecognisedSurgeonsPage,
    roles: [Role.MkSystemAdmin],
    fill: 'path',
    userAuthState: 'Authenticated',
  },
  CheckIn: {
    title: 'Check In',
    path: `/check-in/${DELIVERY_ID_PARAM}`,
    makePath: makePath.deliveryId('CheckIn'),
    component: CheckInPage,
    roles: DELIVERIES_ROLES,
  },
  CheckInWorkflow: {
    title: 'Check In Workflow',
    path: `/${CHECK_IN_WORKFLOW_PATH}/${DELIVERY_ID_PARAM}`,
    makePath: makePath.deliveryId('CheckInWorkflow'),
    component: CheckInPage,
    roles: DELIVERIES_ROLES,
  },
  ViewDispatch: {
    title: 'View Dispatched Order',
    path: `/view-dispatch/${DELIVERY_ID_PARAM}`,
    makePath: makePath.deliveryId('ViewDispatch'),
    component: ViewDispatchPage,
    roles: DELIVERIES_ROLES,
  },
  Implants: {
    title: 'Sterile Implants',
    path: `/implants/${DELIVERY_ID_PARAM}`,
    makePath: makePath.deliveryId('Implants'),
    component: ImplantsPage,
    roles: DELIVERIES_ROLES,
  },
  SurgeryImplants: {
    title: 'Surgery Sterile Implants',
    path: `/surgery/${ORDER_RECORD_ID_PARAM}/implants`,
    makePath: makePath.orderId('SurgeryImplants'),
    component: ImplantsPage,
    roles: DELIVERIES_ROLES,
  },
  ImplantList: {
    title: 'Sterile Implants List',
    path: '/implants-list/:deliveryId',
    makePath: makePath.deliveryId('ImplantList'),
    component: ImplantList,
    roles: DELIVERIES_ROLES,
  },
  SurgeryImplantList: {
    title: 'Surgery Sterile Implants List',
    path: `/surgery/${ORDER_RECORD_ID_PARAM}/implants-list`,
    makePath: makePath.orderId('SurgeryImplantList'),
    component: ImplantList,
    roles: DELIVERIES_ROLES,
  },
  CaptureNonScannableImplants: {
    title: 'Capture Non Scannable Sterile Implants',
    path: `/capture-non-scannable-implants/${DELIVERY_ID_PARAM}`,
    makePath: makePath.deliveryId('CaptureNonScannableImplants'),
    component: CaptureNonScannableImplants,
    roles: DELIVERIES_ROLES,
  },
  CaptureNonScannableSurgeryImplants: {
    title: 'Capture Non Scannable Surgery Sterile Implants',
    path: `/surgery/${ORDER_RECORD_ID_PARAM}/capture-non-scannable-implants`,
    makePath: makePath.orderId('CaptureNonScannableSurgeryImplants'),
    component: CaptureNonScannableImplants,
    roles: DELIVERIES_ROLES,
  },
  CheckInTrayImageCapture: {
    title: '',
    path: `/check-in/${DELIVERY_ID_PARAM}/${SET_ID_PARAM}/${CHECK_IN_TRAY_ID_PARAM}/capture-image`,
    makePath: makePath.deliveryIdSetIdTrayId('CheckInTrayImageCapture'),
    component: CheckInTrayImageCapture,
    roles: DELIVERIES_ROLES,
  },
  CheckInTrayDetails: {
    title: '',
    path: `/check-in/${DELIVERY_ID_PARAM}/${SET_ID_PARAM}/${CHECK_IN_TRAY_ID_PARAM}/details`,
    makePath: makePath.deliveryIdSetIdTrayId('CheckInTrayDetails'),
    component: CheckInTrayDetailsPage,
    roles: DELIVERIES_ROLES,
  },
  CheckInTrayDecantImageCapture: {
    title: '',
    path: `/check-in/${DELIVERY_ID_PARAM}/${SET_ID_PARAM}/${CHECK_IN_TRAY_ID_PARAM}/decant/capture-image`,
    makePath: makePath.deliveryIdSetIdTrayId('CheckInTrayDecantImageCapture'),
    component: CheckInTrayDecantImageCapture,
    roles: DELIVERIES_ROLES,
  },
  CheckInTrayDecantDetails: {
    title: '',
    path: `/check-in/${DELIVERY_ID_PARAM}/${SET_ID_PARAM}/${CHECK_IN_TRAY_ID_PARAM}/decant/details`,
    makePath: makePath.deliveryIdSetIdTrayId('CheckInTrayDecantDetails'),
    component: CheckInTrayDecantDetails,
    roles: DELIVERIES_ROLES,
  },
  CheckInTray: {
    title: 'Check In Tray',
    path: `/check-in-tray/${DELIVERY_ID_PARAM}/${SET_ID_PARAM}/${TRAY_ID_PARAM}`,
    makePath: makePath.deliveryIdSetIdTrayId('CheckInTray'),
    component: CheckInTrayPage,
    roles: DELIVERIES_ROLES,
  },
  CheckOut: {
    title: 'Check Out',
    path: `/check-out/${DELIVERY_ID_PARAM}`,
    makePath: makePath.deliveryId('CheckOut'),
    component: CheckOutPage,
    roles: DELIVERIES_ROLES,
  },
  SetDetails: {
    title: 'Instrument Set Details',
    path: `/set-details/${DELIVERY_ID_PARAM}/${SET_ID_PARAM}`,
    makePath: makePath.deliveryIdSetId('SetDetails'),
    component: SetDetailsPage,
    roles: DELIVERIES_ROLES.concat(ORDERS_ROLES),
  },
  CheckOutTray: {
    title: 'Check Out Tray',
    path: `/check-out-tray/${DELIVERY_ID_PARAM}/${SET_ID_PARAM}/${TRAY_ID_PARAM}`,
    makePath: makePath.deliveryIdSetIdTrayId('CheckOutTray'),
    component: CheckOutTrayPage,
    roles: DELIVERIES_ROLES,
  },
  CheckOutTrayImageCapture: {
    title: '',
    path: `/check-out/${DELIVERY_ID_PARAM}/${SET_ID_PARAM}/${TRAY_ID_PARAM}/capture-image`,
    makePath: makePath.deliveryIdSetIdTrayId('CheckOutTrayImageCapture'),
    component: CheckOutTrayImageCapture,
    roles: DELIVERIES_ROLES,
  },
  CheckOutTraySubmit: {
    title: 'Tray Information',
    path: `/check-out-tray-submit/${DELIVERY_ID_PARAM}/${SET_ID_PARAM}/${TRAY_ID_PARAM}`,
    makePath: makePath.deliveryIdSetIdTrayId('CheckOutTraySubmit'),
    component: CheckOutTraySubmitPage,
    roles: DELIVERIES_ROLES,
  },
  TrayImageCapture: {
    title: 'Capture Tray Image',
    path: `/capture-tray-image/:stage/${DELIVERY_ID_PARAM}/${SET_ID_PARAM}/${TRAY_ID_PARAM}`,
    makePath: (stage: string, deliveryId: string, setId: string, trayId: string) => {
      return makePath.deliveryIdSetIdTrayId('TrayImageCapture')(deliveryId, setId, trayId).replace(':stage', stage);
    },
    component: CaptureTrayImagePage,
    roles: DELIVERIES_ROLES,
  },
  TrayPictureList: {
    title: 'Tray Picture List',
    path: `/tray-picture-list/${DELIVERY_ID_PARAM}/${SET_ID_PARAM}/${TRAY_ID_PARAM}`,
    makePath: makePath.deliveryIdSetIdTrayId('TrayPictureList'),
    component: TrayPictureListPage,
    roles: DELIVERIES_ROLES,
  },
  LastTrayInspection: {
    title: 'Last Supplier Tray Inspection',
    path: `/last-tray-inspection/${DELIVERY_ID_PARAM}/${SET_ID_PARAM}/${TRAY_ID_PARAM}`,
    makePath: makePath.deliveryIdSetIdTrayId('LastTrayInspection'),
    component: LastTrayInspectionPage,
    roles: DELIVERIES_ROLES,
  },
  NotFound: {
    title: '404',
    path: '/404',
    makePath: makePath.noArgs('NotFound'),
    component: NotFoundPage,
    userAuthState: 'Not Authenticated',
  },
  ColorPalette: {
    title: 'Color Palette',
    path: '/color-palette',
    makePath: makePath.noArgs('ColorPalette'),
    component: ColorPalettePage,
    userAuthState: 'Not Authenticated',
  },
  Orders: {
    title: 'Orders',
    path: '/orders',
    makePath: makePath.noArgs('Orders'),
    component: OrdersPage,
    roles: ORDERS_ROLES,
    Icon: DesktopIcon,
    fill: 'path',
  },
  UploadOrders: {
    title: 'Upload Orders',
    path: '/orders/upload',
    makePath: makePath.noArgs('UploadOrders'),
    component: UploadOrdersPage,
    roles: ORDERS_ROLES,
    userAuthState: 'Logged',
  },
  ReviewOrders: {
    title: 'Review Orders',
    path: '/orders/upload/review',
    makePath: makePath.noArgs('ReviewOrders'),
    component: ReviewOrdersPage,
    roles: ORDERS_ROLES,
    userAuthState: 'Logged',
  },
  ConfirmInDock: {
    title: 'Confirm In Dock',
    path: '/orders/confirm-in-dock',
    makePath: makePath.noArgs('ConfirmInDock'),
    component: ConfirmInDockPage,
    roles: ORDERS_ROLES,
    userAuthState: 'Logged',
  },
  OrderV1: {
    title: 'Order',
    path: `/${NEW_ORDER_PAGE_ON ? 'ordersv1' : 'orders'}/${ORDER_RECORD_ID_PARAM}`,
    makePath: makePath.orderId('OrderV1'),
    component: OrderPageV1,
    roles: [...ORDERS_ROLES, ...DELIVERIES_ROLES],
  },
  Order: {
    title: 'Order',
    path: `/${NEW_ORDER_PAGE_ON ? 'orders' : 'ordersv2'}/${ORDER_RECORD_ID_PARAM}`,
    makePath: makePath.orderId('Order'),
    component: OrderPage,
    roles: [...ORDERS_ROLES, ...DELIVERIES_ROLES],
  },
  OrderSet: {
    title: 'Order Set',
    path: `/orders/${ORDER_RECORD_ID_PARAM}/set/${SET_ID_PARAM}`,
    makePath: makePath.orderIdSetId('OrderSet'),
    component: OrderSetPage,
    roles: ORDERS_ROLES,
  },
  OrderImplantList: {
    title: 'Sterile Implants List',
    path: `/orders/${ORDER_RECORD_ID_PARAM}/implants-list`,
    makePath: makePath.orderId('OrderImplantList'),
    component: ImplantList,
    roles: ORDERS_ROLES,
  },
  Surgeons: {
    title: 'Surgeons',
    path: '/contacts/surgeons',
    makePath: makePath.noArgs('Surgeons'),
    component: SurgeonsPage,
    roles: HOSPITAL_MANAGER_ROLES,
  },
  DeviceReps: {
    title: 'Device Manufacturer Reps',
    path: '/contacts/device-reps',
    makePath: makePath.noArgs('DeviceReps'),
    component: DeviceRepsPage,
    roles: HOSPITAL_MANAGER_ROLES,
  },
  Contacts: {
    title: 'Contacts',
    path: '/contacts',
    makePath: makePath.noArgs('Contacts'),
    component: ContactsPage,
    roles: HOSPITAL_MANAGER_ROLES,
    Icon: ContactsIcon,
  },
  Turnaround: {
    title: 'Turnaround',
    path: '/turnaround',
    makePath: makePath.noArgs('Turnaround'),
    component: TurnaroundPage,
    roles: [Role.MkSystemAdmin, Role.SaviConnectManager, Role.SaviConnectUser],
    Icon: TurnaroundIcon,
  },
  TurnaroundList: {
    title: 'Turnaround List',
    path: '/turnaround/list',
    makePath: makePath.noArgs('TurnaroundList'),
    component: TurnaroundListPage,
    roles: [Role.MkSystemAdmin, Role.SaviConnectManager, Role.SaviConnectUser],
  },
  LoggedOut: {
    title: 'Logged Out',
    path: '/logged-out/:reason',
    makePath: (reason?: LoggedOutReason) => routes.LoggedOut.path.replace(':reason', reason || 'login'),
    component: LoggedOutPage,
    userAuthState: 'Not Authenticated',
  },
};

export default routes;
